// components/crm/dialogs/TaskDetailsDialog.jsx
import {
  CalendarDaysIcon,
  CheckCircleIcon,
  CheckIcon,
  ClipboardDocumentListIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { api, getApiErrorMessage } from '../../../lib/api';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Dialog, DialogContent } from '../../ui/dialog';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Textarea } from '../../ui/textarea';

const getStatusColor = (status) => {
  const c = {
    pending:     'bg-yellow-100 text-yellow-800',
    completed:   'bg-green-100 text-green-800',
    overdue:     'bg-red-100 text-red-800',
    in_progress: 'bg-blue-100 text-blue-800',
  };
  return c[status] || 'bg-gray-100 text-gray-800';
};

const getPriorityColor = (priority) => {
  const c = { high: 'bg-red-100 text-red-800', medium: 'bg-yellow-100 text-yellow-800', low: 'bg-green-100 text-green-800' };
  return c[priority] || 'bg-gray-100 text-gray-800';
};

const getTypeColor = (type) => {
  const c = {
    call:      'bg-blue-100 text-blue-800',
    demo:      'bg-purple-100 text-purple-800',
    follow_up: 'bg-orange-100 text-orange-800',
    meeting:   'bg-green-100 text-green-800',
    email:     'bg-sky-100 text-sky-800',
    other:     'bg-gray-100 text-gray-800',
  };
  return c[type] || 'bg-gray-100 text-gray-800';
};

const TASK_TYPE_ICONS = {
  call: '📞', demo: '🖥️', follow_up: '🔁', meeting: '🤝', email: '✉️', other: '✦',
};

const formatDateTime = (ds) =>
  new Date(ds).toLocaleString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });

const isDueOverdue = (dueDate) => dueDate && new Date(dueDate) < new Date();

/** Thin horizontal section divider with centred label */
const SectionLabel = ({ icon: Icon, label }) => (
  <div className="flex items-center gap-2">
    <div className="h-px flex-1 bg-gray-100" />
    <span className="flex items-center gap-1 text-[10px] font-semibold text-blue-600 uppercase tracking-widest px-2">
      {Icon && <Icon className="h-3 w-3" />}
      {label}
    </span>
    <div className="h-px flex-1 bg-gray-100" />
  </div>
);

/** Read-only info cell */
const InfoCell = ({ label, children }) => (
  <div>
    <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wide mb-1">{label}</p>
    <div>{children}</div>
  </div>
);

export default function TaskDetailsDialog({ task, open, onOpenChange, users, onTaskUpdated }) {
  const [selectedTask,    setSelectedTask]    = useState(task);
  const [completionData,  setCompletionData]  = useState({ outcome: 'interested', remarks: '' });
  const [completing,      setCompleting]      = useState(false);
  const [assigning,       setAssigning]       = useState(false);

  useEffect(() => { setSelectedTask(task); }, [task]);

  const handleAssignTask = async (userId) => {
    setAssigning(true);
    try {
      await api.put(`/tasks/${selectedTask._id}/assign`, { assignedTo: userId });
      toast.success('Task assigned successfully!');
      onTaskUpdated();
    } catch (error) {
      console.error('Error assigning task:', error);
      toast.error('Failed to assign task');
    } finally {
      setAssigning(false);
    }
  };

  const handleCompleteTask = async () => {
    setCompleting(true);
    try {
      await api.put(`/tasks/${selectedTask._id}/complete`, completionData);
      toast.success('Task completed successfully!');
      onTaskUpdated();
      onOpenChange(false);
    } catch (error) {
      console.error('Error completing task:', error);
      toast.error(getApiErrorMessage(error, 'Failed to complete task'));
    } finally {
      setCompleting(false);
    }
  };

  if (!selectedTask) return null;

  const isOverdue  = isDueOverdue(selectedTask.dueDate) && selectedTask.status === 'pending';
  const isPending  = selectedTask.status === 'pending';
  const isComplete = selectedTask.status === 'completed';

  // Gradient by status
  const headerGradient = isComplete
    ? 'bg-gradient-to-r from-green-600 to-emerald-600'
    : isOverdue
    ? 'bg-gradient-to-r from-red-600 to-rose-600'
    : 'bg-gradient-to-r from-blue-600 to-violet-600';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent hideCloseButton className="max-w-[95vw] sm:max-w-[520px] md:max-w-[600px] max-h-[92vh] p-0 gap-0 flex flex-col overflow-hidden">

        {/* ── Gradient Header ──────────────────────────────────────────────────── */}
        <div className={`${headerGradient} px-6 py-4 flex-shrink-0`}>
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0 text-lg">
                {TASK_TYPE_ICONS[selectedTask.taskType] || '✦'}
              </div>
              <div>
                <h2 className="text-base font-semibold text-white leading-tight">
                  {selectedTask.title || 'Follow-up Task'}
                </h2>
                <p className="text-xs text-white/70 mt-0.5">
                  {selectedTask.leadId?.name}
                  {selectedTask.leadId?.name && selectedTask.leadId?.email ? ' · ' : ''}
                  {selectedTask.leadId?.email}
                </p>
              </div>
            </div>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => onOpenChange(false)}
              className="h-8 px-3 text-xs bg-white/20 hover:bg-white/30 text-white border-0 flex-shrink-0"
            >
              Close
            </Button>
          </div>
        </div>

        {/* ── Scrollable Body ───────────────────────────────────────────────────── */}
        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 space-y-5">

          {/* Status chips */}
          <div className="flex flex-wrap gap-2">
            <Badge className={`crm-table-badge crm-table-badge--status font-normal capitalize ${getStatusColor(selectedTask.status)}`}>
              {selectedTask.status}
            </Badge>
            {isOverdue && <span className="crm-badge-overdue">Overdue</span>}
            <Badge className={`crm-table-badge crm-table-badge--type font-normal capitalize ${getTypeColor(selectedTask.taskType)}`}>
              {(selectedTask.taskType || '').replace('_', ' ')}
            </Badge>
            <Badge className={`font-normal capitalize ${getPriorityColor(selectedTask.priority)}`}>
              {selectedTask.priority} priority
            </Badge>
          </div>

          {/* Task Details */}
          <div>
            <SectionLabel icon={ClipboardDocumentListIcon} label="Task Details" />
            <div className="mt-3 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <InfoCell label="Due Date">
                  <p className={`text-sm font-medium ${isOverdue ? 'text-red-600' : 'text-gray-800'}`}>
                    {formatDateTime(selectedTask.dueDate)}
                  </p>
                </InfoCell>
                <InfoCell label="Created At">
                  <p className="text-sm font-medium text-gray-800">
                    {selectedTask.createdAt ? formatDateTime(selectedTask.createdAt) : '—'}
                  </p>
                </InfoCell>
              </div>
              {(selectedTask.description || selectedTask.remarks) && (
                <div className="bg-gray-50 border border-gray-100 rounded-lg p-3">
                  <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wide mb-1">Description</p>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">
                    {selectedTask.description || selectedTask.remarks}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Assignment */}
          <div>
            <SectionLabel icon={UserGroupIcon} label="Assignment" />
            <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex-1">
                <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wide mb-1">Assigned To</p>
                <p className="text-sm font-medium text-gray-800">
                  {selectedTask.assignedTo?.name || <span className="text-gray-400 italic">Unassigned</span>}
                </p>
              </div>
              {isPending && (
                <Select onValueChange={handleAssignTask} disabled={assigning}>
                  <SelectTrigger className="h-9 text-sm w-full sm:w-52">
                    {assigning ? (
                      <span className="flex items-center gap-2">
                        <svg className="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24" fill="none">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        Assigning…
                      </span>
                    ) : (
                      <SelectValue placeholder="Reassign to…" />
                    )}
                  </SelectTrigger>
                  <SelectContent>
                    {users.map(user => (
                      <SelectItem key={user._id} value={user._id} className="text-sm">
                        {user.name} — {user.role}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>

          {/* Complete Task form */}
          {isPending && (
            <div>
              <SectionLabel icon={CheckCircleIcon} label="Complete Task" />
              <div className="mt-3 space-y-3 bg-blue-50 border border-blue-100 rounded-xl p-4">
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-gray-700">Outcome</Label>
                  <Select
                    value={completionData.outcome}
                    onValueChange={(value) => setCompletionData({ ...completionData, outcome: value })}
                  >
                    <SelectTrigger className="h-9 text-sm bg-white"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="interested"      className="text-sm">Interested</SelectItem>
                      <SelectItem value="not_interested"  className="text-sm">Not Interested</SelectItem>
                      <SelectItem value="callback"        className="text-sm">Callback Requested</SelectItem>
                      <SelectItem value="demo_scheduled"  className="text-sm">Demo Scheduled</SelectItem>
                      <SelectItem value="converted"       className="text-sm">Converted</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-gray-700">Remarks</Label>
                  <Textarea
                    value={completionData.remarks}
                    onChange={(e) => setCompletionData({ ...completionData, remarks: e.target.value })}
                    placeholder="Enter completion remarks…"
                    rows={3}
                    className="text-sm resize-none bg-white"
                  />
                </div>
                <Button
                  onClick={handleCompleteTask}
                  size="sm"
                  disabled={completing}
                  className="w-full text-sm bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white gap-2"
                >
                  {completing ? (
                    <>
                      <svg className="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Completing…
                    </>
                  ) : (
                    <>
                      <CheckIcon className="h-3.5 w-3.5" />
                      Mark as Completed
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Completion Details (if already done) */}
          {isComplete && selectedTask.completedAt && (
            <div>
              <SectionLabel icon={CalendarDaysIcon} label="Completion Details" />
              <div className="mt-3 bg-green-50 border border-green-100 rounded-xl p-4 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <InfoCell label="Completed At">
                    <p className="text-sm font-medium text-gray-800">{formatDateTime(selectedTask.completedAt)}</p>
                  </InfoCell>
                  <InfoCell label="Outcome">
                    <p className="text-sm font-medium text-gray-800 capitalize">{selectedTask.outcome || '—'}</p>
                  </InfoCell>
                </div>
                {selectedTask.remarks && (
                  <InfoCell label="Remarks">
                    <p className="text-sm text-gray-700">{selectedTask.remarks}</p>
                  </InfoCell>
                )}
              </div>
            </div>
          )}

        </div>
      </DialogContent>
    </Dialog>
  );
}
